import React, {
  useState,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react'
import {
  FlatList,
  View,
  RefreshControl,
  ActivityIndicator,
  FlatListProps,
} from 'react-native'
import { Text } from 'react-native-paper'
import { createStyle } from '../../../contexts/global-styles.provider'
import colors from '../../../constants/colors'
import Box from '../grid/box'
import { useTranslation } from 'react-i18next'

interface PaginationParams {
  offset: number
  limit: number
}

interface PaginatedData<T> {
  results: T[]
  total: number
}

interface PaginatedFlatListProps<T>
  extends Omit<
    FlatListProps<T>,
    'data' | 'onRefresh' | 'refreshControl' | 'onEndReached'
  > {
  // Data
  data: T[]
  total: number

  // Pagination config
  initialLimit?: number
  loadMoreThreshold?: number

  // Callbacks
  onLoadMore: (params: PaginationParams) => Promise<void> | void
  onRefresh: () => Promise<void> | void

  // Empty state
  emptyComponent?: React.ComponentType | React.ReactElement
  emptyMessage?: string
  emptySubMessage?: string

  // Loading components
  loadingComponent?: React.ComponentType | React.ReactElement
}

export const PaginatedFlatList = forwardRef(function PaginatedFlatList<T>(
  {
    data = [],
    total = 0,
    initialLimit = 20,
    loadMoreThreshold = 0.7,
    onLoadMore,
    onRefresh,
    emptyComponent,
    emptyMessage,
    emptySubMessage,
    loadingComponent,
    renderItem,
    ...flatListProps
  }: PaginatedFlatListProps<T>,
  ref: React.Ref<FlatList<T>>
) {
  const { t } = useTranslation('common')

  const [refreshing, setRefreshing] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)

  const hasMoreData = data.length < total

  const handleLoadMore = useCallback(async () => {
    if (!hasMoreData) return
    if (loadingMore) return
    setLoadingMore(true)
    try {
      await onLoadMore({
        offset: data.length,
        limit: initialLimit,
      })
    } finally {
      setLoadingMore(false)
    }
  }, [data.length, total, onLoadMore, initialLimit, loadingMore, hasMoreData])

  const handleRefresh = useCallback(async () => {
    setRefreshing(true)
    try {
      await onRefresh()
    } finally {
      setRefreshing(false)
    }
  }, [onRefresh])

  const flatListRef = useRef<FlatList<T>>(null)

  // Expose the FlatList ref to the parent
  useImperativeHandle(ref, () => flatListRef.current as FlatList<T>)

  return (
    <FlatList
      ref={flatListRef}
      data={data}
      renderItem={renderItem}
      keyExtractor={(item, index) => `item-${index}`}
      // Pagination
      onEndReached={handleLoadMore}
      onEndReachedThreshold={loadMoreThreshold}
      // Refresh
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={handleRefresh}
          colors={[colors.primary]}
          tintColor={colors.primary}
        />
      }
      // Components
      ItemSeparatorComponent={() => <View style={{ height: 10 }} />}
      ListEmptyComponent={
        <EmptyStateComponent
          loading={loadingMore}
          loadingComponent={loadingComponent}
          emptyComponent={emptyComponent}
          emptyMessage={emptyMessage || t('no-items-found')}
          emptySubMessage={emptySubMessage}
        />
      }
      ListFooterComponent={<FooterComponent hasMoreData={hasMoreData} />}
      // Performance
      removeClippedSubviews={true}
      maxToRenderPerBatch={10}
      updateCellsBatchingPeriod={50}
      initialNumToRender={initialLimit}
      windowSize={10}
      // Other props
      {...flatListProps}
    />
  )
})

interface EmptyStateProps {
  loading: boolean
  loadingComponent?: React.ComponentType | React.ReactElement
  emptyComponent?: React.ComponentType | React.ReactElement
  emptyMessage: string
  emptySubMessage?: string
}

const EmptyStateComponent: React.FC<EmptyStateProps> = ({
  loading,
  loadingComponent,
  emptyComponent,
  emptyMessage,
  emptySubMessage,
}) => {
  const styles = useStyle()

  const emptyCustomComponent = useMemo(() => {
    if (!emptyComponent) {
      return null
    }

    return React.isValidElement(emptyComponent)
      ? emptyComponent
      : React.createElement(emptyComponent as React.ComponentType)
  }, [emptyComponent])

  const loadingCustomComponent = useMemo(() => {
    if (!loadingComponent) {
      return null
    }

    return React.isValidElement(loadingComponent)
      ? loadingComponent
      : React.createElement(loadingComponent as React.ComponentType)
  }, [loadingComponent])

  if (loading) {
    if (loadingCustomComponent) {
      return loadingCustomComponent
    }
    return (
      <Box p={8} mt={32} style={styles.centerContainer}>
        <Box mt={32}> </Box>
        <ActivityIndicator size="large" color={colors.primary} />
      </Box>
    )
  }

  if (emptyCustomComponent) {
    return emptyCustomComponent
  }

  return (
    <Box p={8} style={styles.centerContainer}>
      <Box mt={32}> </Box>
      <Text variant="titleMedium" style={styles.emptyMessage}>
        {emptyMessage}
      </Text>
      {emptySubMessage && (
        <Text variant="bodyMedium" style={styles.emptySubMessage}>
          {emptySubMessage}
        </Text>
      )}
      <Box mt={32}> </Box>
    </Box>
  )
}

interface FooterProps {
  hasMoreData: boolean
}

const FooterComponent: React.FC<FooterProps> = ({ hasMoreData }) => {
  if (!hasMoreData) return null

  return <Box pb={4} /> // Bottom spacing
}

const useStyle = createStyle(({ theme }) => ({
  centerContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyMessage: {
    textAlign: 'center',
    color: colors.grey600,
    marginBottom: theme.spacing(1),
  },
  emptySubMessage: {
    textAlign: 'center',
    color: colors.grey600,
  },
  loadingText: {
    color: colors.grey600,
    textAlign: 'center',
  },
}))

export default PaginatedFlatList
